import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorObservable } from './message.observable';
import { ErrorModel, ErrorSeverity } from '../model.module/modals.model';
import { AppConstants } from '../app-constants.service';
import { SILENT_HTTP_ERROR } from './http-error.token';

/**
 * Globaler HTTP-Fehler-Interceptor.
 *
 * Zeigt bei jedem fehlgeschlagenen REST-Request automatisch einen Fehlerdialog
 * via {@link ErrorObservable} an, sofern der Request nicht explizit als „stumm"
 * markiert wurde.
 *
 * Um den Fehlerdialog für einen bestimmten Request zu unterdrücken:
 * @example
 *   import { HttpContext } from '@angular/common/http';
 *   import { SILENT_HTTP_ERROR } from './http-error.token';
 *
 *   this.apiService.doSomething(payload, undefined, new HttpContext().set(SILENT_HTTP_ERROR, true));
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

    constructor(
        private readonly errorObservable: ErrorObservable,
        private readonly appConstants: AppConstants
    ) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const silent = req.context.get(SILENT_HTTP_ERROR);

        return next.handle(req).pipe(
            catchError((error: unknown) => {
                if (!silent && error instanceof HttpErrorResponse) {
                    // HttpErrorResponse ist strukturell kompatibel zu Response (status, headers.get()),
                    // wie es error.component.ts erwartet.
                    const errorModel = new ErrorModel(
                        this.mapSeverity(error.status),
                        this.appConstants.messages.fehlerAufgetreten,
                        [this.appConstants.messages.spaeterErneutVersuchen],
                        error as unknown as Response
                    );
                    this.errorObservable.notify(errorModel);
                }

                return throwError(() => error);
            })
        );
    }

    /**
     * Bildet einen HTTP-Statuscode auf einen {@link ErrorSeverity}-Wert ab.
     * 5xx → SERIOUS, alles andere → COMMON.
     */
    private mapSeverity(status: number): ErrorSeverity {
        if (status >= 500) {
            return ErrorSeverity.SERIOUS;
        }
        return ErrorSeverity.COMMON;
    }
}

